import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { captureSmartTablePresetCreated } from "./analytics";
import {
  CreateBrandVisibilityPresetRequest,
  CreateBrandVisibilityPresetResponse,
  SmartTableDetail,
  unwrapSmartTableEntityPayload,
} from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { routePaths } from "@/utils/routePaths";
import { displayToast } from "@/utils/toast";

export const useCreateBrandVisibilityPreset = ({ openInNewTab = false } = {}) => {
  const userToken = useAuth()?.userToken ?? "";
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wid = Number(workspaceId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<SmartTableDetail, unknown, CreateBrandVisibilityPresetRequest>({
    mutationFn: async payload => {
      const response = await fetch(ENDPOINTS.smart_table_brand_visibility_preset(wid), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(payload),
      });
      const responsePayload = (await handleResponse(
        response,
      )) as CreateBrandVisibilityPresetResponse;
      return unwrapSmartTableEntityPayload<SmartTableDetail>(responsePayload, "smart_table");
    },
    onSuccess: smartTable => {
      queryClient.invalidateQueries({ queryKey: ["smart-tables", wid, "list"] });

      captureSmartTablePresetCreated({
        workspaceId: wid,
        smartTableId: smartTable.id,
        presetType: "brand-visibility",
      });

      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: ["folder-entities", workspaceId] });
        const url = routePaths.workspaceSmartTable(workspaceId, smartTable.id);
        if (openInNewTab) {
          window.open(url, "_blank");
        } else {
          navigate(url);
        }
      }

      displayToast("Brand visibility table created", ToastType.success);
    },
  });
};
